import React, { useEffect, useMemo, useRef, useState } from "react";
import { http, fileUrl, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Upload, Image as ImageIcon, Loader2, Check, Trash2, FileImage, RotateCcw,
  Download, Eye, Type, Palette, Layout, Layers, FileText,
} from "lucide-react";
import { toast } from "sonner";
import A4Editor from "../components/config/A4Editor";

const DEFAULTS = {
  logo_path: "", template_path: "", agency_name: "",
  show_agency_name: true, show_logo: true,
  font_family: "Helvetica", page_bg_color: "#FFFFFF",
  title_text: "Cotización de Viaje", title_align: "left",
  title_x_mm: 15, title_y_mm: 45, title_font_size: 22,
  header_text: "", header_align: "center",
  header_x_mm: 105, header_y_mm: 10, header_font_size: 9,
  footer_text: "Sujeto a disponibilidad a la hora de confirmación",
  footer_align: "center", footer_x_mm: 105, footer_y_mm_from_bottom: 12, footer_font_size: 9,
  agency_x_mm: 195, agency_y_mm: 20, agency_font_size: 11, agency_align: "right",
  logo_x_mm: 15, logo_y_mm: 10, logo_width_mm: 38,
  accent_color: "#0284C7", text_color: "#0F172A", muted_color: "#475569",
  price_color: "#D97706", dark_bg_color: "#121C2B", card_bg_color: "#F4F3EF",
  passenger_label: "PASAJERO", destination_label: "DESTINO", passengers_label: "PASAJEROS",
  flights_overline: "ITINERARIO DE VUELOS", flights_title: "Vuelos",
  hotels_overline: "ALOJAMIENTOS", hotels_title: "Hoteles",
  notes_overline: "NOTAS", total_label: "TOTAL COTIZACIÓN",
  flight_price_pp_label: "Precio por persona", flight_price_total_label: "Precio total",
  hotel_price_pp_label: "Precio por persona", hotel_price_total_label: "Precio total",
  show_page_number: true,
};

const ColorField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
             data-testid={testId} className="h-9 w-12 rounded-lg border border-slate-200 cursor-pointer bg-white" />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="rounded-lg h-9 font-mono-2 text-xs uppercase" maxLength={7} />
    </div>
  </div>
);

const NumberField = ({ label, value, onChange, min = 0, max = 297, step = 0.5, suffix = "mm", testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
    <div className="relative">
      <Input type="number" value={value ?? 0} min={min} max={max} step={step}
             onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
             className="rounded-lg h-9 pr-9" data-testid={testId} />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 font-mono-2">{suffix}</span>
    </div>
  </div>
);

const AlignSelect = ({ value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Alineación</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-lg h-9" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="left">Izquierda</SelectItem>
        <SelectItem value="center">Centro</SelectItem>
        <SelectItem value="right">Derecha</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

const SettingsPage = () => {
  const [s, setS] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null); // 'logo_path' | 'template_path'
  const [selected, setSelected] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const logoRef = useRef(null);
  const tplRef = useRef(null);

  const set = (patch) => setS((p) => ({ ...p, ...patch }));

  useEffect(() => {
    http.get("/settings").then((r) => setS({ ...DEFAULTS, ...r.data }));
  }, []);

  const uploadFile = async (file, key) => {
    setUploadingKey(key);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await http.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      set({ [key]: r.data.storage_path });
      toast.success("Imagen cargada. No olvides guardar.");
    } catch { toast.error("No se pudo subir la imagen"); }
    finally { setUploadingKey(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/settings", s);
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const resetStyle = () => { setS((p) => ({ ...DEFAULTS, logo_path: p.logo_path, template_path: p.template_path, agency_name: p.agency_name })); toast.info("Estilos restablecidos. Guardá para aplicar."); };

  const generatePreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/preview.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings: s }),
      });
      const blob = await res.blob();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error("No se pudo generar preview"); }
    finally { setPreviewLoading(false); }
  };

  // Property inspector for the selected element
  const inspector = useMemo(() => {
    if (!selected) return null;
    if (selected === "title") return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Texto del título</Label>
          <Input value={s.title_text} onChange={(e) => set({ title_text: e.target.value })} className="rounded-lg h-9" data-testid="input-title-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="X" value={s.title_x_mm} onChange={(v) => set({ title_x_mm: v })} max={210} testId="num-title-x" />
          <NumberField label="Y (desde arriba)" value={s.title_y_mm} onChange={(v) => set({ title_y_mm: v })} max={297} testId="num-title-y" />
          <AlignSelect value={s.title_align} onChange={(v) => set({ title_align: v })} testId="sel-title-align" />
          <NumberField label="Tamaño" value={s.title_font_size} onChange={(v) => set({ title_font_size: v })} max={72} step={1} suffix="pt" testId="num-title-size" />
        </div>
      </div>
    );
    if (selected === "header") return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Texto del encabezado (multi-línea)</Label>
          <Textarea rows={3} value={s.header_text} onChange={(e) => set({ header_text: e.target.value })} className="rounded-lg" placeholder="Podés usar varias líneas" data-testid="input-header-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="X" value={s.header_x_mm} onChange={(v) => set({ header_x_mm: v })} max={210} />
          <NumberField label="Y (desde arriba)" value={s.header_y_mm} onChange={(v) => set({ header_y_mm: v })} max={297} />
          <AlignSelect value={s.header_align} onChange={(v) => set({ header_align: v })} />
          <NumberField label="Tamaño" value={s.header_font_size} onChange={(v) => set({ header_font_size: v })} max={48} step={0.5} suffix="pt" />
        </div>
      </div>
    );
    if (selected === "footer") return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Pie de página</Label>
          <Textarea rows={2} value={s.footer_text} onChange={(e) => set({ footer_text: e.target.value })} className="rounded-lg" data-testid="input-footer-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="X" value={s.footer_x_mm} onChange={(v) => set({ footer_x_mm: v })} max={210} />
          <NumberField label="Y (desde abajo)" value={s.footer_y_mm_from_bottom} onChange={(v) => set({ footer_y_mm_from_bottom: v })} max={100} />
          <AlignSelect value={s.footer_align} onChange={(v) => set({ footer_align: v })} />
          <NumberField label="Tamaño" value={s.footer_font_size} onChange={(v) => set({ footer_font_size: v })} max={24} step={0.5} suffix="pt" />
        </div>
      </div>
    );
    if (selected === "agency") return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Nombre de la agencia</Label>
          <Input value={s.agency_name} onChange={(e) => set({ agency_name: e.target.value })} className="rounded-lg h-9" data-testid="input-agency-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="X" value={s.agency_x_mm} onChange={(v) => set({ agency_x_mm: v })} max={210} />
          <NumberField label="Y (desde arriba)" value={s.agency_y_mm} onChange={(v) => set({ agency_y_mm: v })} max={297} />
          <AlignSelect value={s.agency_align} onChange={(v) => set({ agency_align: v })} />
          <NumberField label="Tamaño" value={s.agency_font_size} onChange={(v) => set({ agency_font_size: v })} max={48} step={0.5} suffix="pt" />
        </div>
      </div>
    );
    if (selected === "logo") return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="X" value={s.logo_x_mm} onChange={(v) => set({ logo_x_mm: v })} max={210} />
          <NumberField label="Y (desde arriba)" value={s.logo_y_mm} onChange={(v) => set({ logo_y_mm: v })} max={297} />
          <NumberField label="Ancho" value={s.logo_width_mm} onChange={(v) => set({ logo_width_mm: v })} max={120} step={1} />
        </div>
      </div>
    );
    return null;
  }, [selected, s]);

  const elementLabels = {
    title: "Título",
    header: "Encabezado",
    footer: "Pie de página",
    agency: "Nombre de agencia",
    logo: "Logo",
  };

  return (
    <div className="fade-up pb-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-1">Modo edición</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Editor visual del PDF</h1>
          <p className="mt-1 text-sm text-slate-600">Arrastrá los elementos sobre la hoja A4, ajustá tipografía y colores desde el panel derecho.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={generatePreview} className="rounded-xl h-10" data-testid="button-live-preview">
            {previewLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Eye size={16} className="mr-2" />}
            Ver PDF real
          </Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-10 px-5" data-testid="button-save-settings">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}
            Guardar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4">
        {/* Editor Canvas */}
        <div className="bg-white border border-slate-200 rounded-2xl p-3 min-h-[720px] xl:min-h-[820px] flex flex-col">
          <div className="flex items-center justify-between px-2 pb-2">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Layout size={14} /> Vista A4 · {selected ? <span className="text-sky-600 font-semibold">{elementLabels[selected]} seleccionado</span> : "Click en un elemento para editarlo"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-2">210 × 297 mm</div>
          </div>
          <div className="flex-1 min-h-[640px]">
            <A4Editor settings={s} onChange={set} selected={selected} setSelected={setSelected} />
          </div>
        </div>

        {/* Sidebar */}
        <aside className="bg-white border border-slate-200 rounded-2xl p-4 space-y-4">
          {/* Element inspector */}
          {inspector && (
            <div className="pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center">
                    <Layers size={14} className="text-sky-600" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest text-sky-600">Propiedades · {elementLabels[selected]}</p>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelected(null)}>Cerrar</Button>
              </div>
              {inspector}
            </div>
          )}

          <Tabs defaultValue="brand">
            <TabsList className="grid grid-cols-5 rounded-xl bg-slate-100 h-9">
              <TabsTrigger value="brand" className="rounded-lg text-[11px]" data-testid="tab-brand"><ImageIcon size={12} className="mr-1" />Marca</TabsTrigger>
              <TabsTrigger value="type" className="rounded-lg text-[11px]" data-testid="tab-type"><Type size={12} className="mr-1" />Tipo</TabsTrigger>
              <TabsTrigger value="colors" className="rounded-lg text-[11px]" data-testid="tab-colors"><Palette size={12} className="mr-1" />Color</TabsTrigger>
              <TabsTrigger value="labels" className="rounded-lg text-[11px]" data-testid="tab-labels"><FileText size={12} className="mr-1" />Textos</TabsTrigger>
              <TabsTrigger value="page" className="rounded-lg text-[11px]" data-testid="tab-page"><Layout size={12} className="mr-1" />Página</TabsTrigger>
            </TabsList>

            <TabsContent value="brand" className="space-y-5 pt-4">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Mostrar nombre en PDF</p>
                <Switch checked={s.show_agency_name} onCheckedChange={(v) => set({ show_agency_name: v })} data-testid="switch-show-agency-name" />
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Mostrar logo en PDF</p>
                <Switch checked={s.show_logo} onCheckedChange={(v) => set({ show_logo: v })} data-testid="switch-show-logo" />
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Logo</Label>
                <div className="flex gap-3 items-start">
                  <div className="w-16 h-16 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden shrink-0">
                    {s.logo_path ? <img src={fileUrl(s.logo_path)} alt="logo" className="max-w-full max-h-full object-contain" /> : <ImageIcon className="text-slate-400" size={20} />}
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <input ref={logoRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "logo_path")} className="hidden" data-testid="logo-file-input" />
                    <Button size="sm" variant="outline" onClick={() => logoRef.current?.click()} disabled={uploadingKey === "logo_path"} className="rounded-lg h-8 text-xs" data-testid="button-upload-logo">
                      {uploadingKey === "logo_path" ? <Loader2 className="animate-spin mr-1" size={12} /> : <Upload size={12} className="mr-1" />}
                      {s.logo_path ? "Cambiar" : "Subir"}
                    </Button>
                    {s.logo_path && <Button size="sm" variant="ghost" onClick={() => set({ logo_path: "" })} className="rounded-lg h-8 text-xs text-rose-600 justify-start"><Trash2 size={12} className="mr-1" />Quitar</Button>}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Plantilla de fondo (A4)</Label>
                <div className="flex gap-3 items-start">
                  <div className="w-16 h-24 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden shrink-0">
                    {s.template_path ? <img src={fileUrl(s.template_path)} alt="tpl" className="w-full h-full object-cover" /> : <FileImage className="text-slate-400" size={22} />}
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <input ref={tplRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "template_path")} className="hidden" data-testid="template-file-input" />
                    <Button size="sm" variant="outline" onClick={() => tplRef.current?.click()} disabled={uploadingKey === "template_path"} className="rounded-lg h-8 text-xs" data-testid="button-upload-template">
                      {uploadingKey === "template_path" ? <Loader2 className="animate-spin mr-1" size={12} /> : <Upload size={12} className="mr-1" />}
                      {s.template_path ? "Cambiar" : "Subir"}
                    </Button>
                    {s.template_path && <Button size="sm" variant="ghost" onClick={() => set({ template_path: "" })} className="rounded-lg h-8 text-xs text-rose-600 justify-start"><Trash2 size={12} className="mr-1" />Quitar</Button>}
                    <p className="text-[10px] text-slate-500">Idealmente 2480×3508 px.</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="type" className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Familia tipográfica</Label>
                <Select value={s.font_family} onValueChange={(v) => set({ font_family: v })}>
                  <SelectTrigger className="rounded-lg h-10" data-testid="select-font-family"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Helvetica" style={{ fontFamily: "Helvetica, Arial, sans-serif" }}>Helvetica (sans-serif moderno)</SelectItem>
                    <SelectItem value="Times-Roman" style={{ fontFamily: "'Times New Roman', serif" }}>Times Roman (serif clásico)</SelectItem>
                    <SelectItem value="Courier" style={{ fontFamily: "Courier, monospace" }}>Courier (monoespaciado)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Se aplica a todo el documento.</p>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Los tamaños de cada texto se editan seleccionando el elemento en la hoja.
              </p>
            </TabsContent>

            <TabsContent value="colors" className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-700">Paleta del documento</p>
                <Button variant="ghost" size="sm" onClick={resetStyle} className="h-7 text-xs" data-testid="button-reset-style"><RotateCcw size={12} className="mr-1" />Reset</Button>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <ColorField label="Acento (etiquetas / barras)" value={s.accent_color} onChange={(v) => set({ accent_color: v })} testId="color-accent" />
                <ColorField label="Texto principal" value={s.text_color} onChange={(v) => set({ text_color: v })} testId="color-text" />
                <ColorField label="Texto secundario" value={s.muted_color} onChange={(v) => set({ muted_color: v })} testId="color-muted" />
                <ColorField label="Precios" value={s.price_color} onChange={(v) => set({ price_color: v })} testId="color-price" />
                <ColorField label="Fondo barra total" value={s.dark_bg_color} onChange={(v) => set({ dark_bg_color: v })} testId="color-dark-bg" />
                <ColorField label="Fondo tarjeta pasajero" value={s.card_bg_color} onChange={(v) => set({ card_bg_color: v })} testId="color-card-bg" />
              </div>
            </TabsContent>

            <TabsContent value="labels" className="space-y-3 pt-4">
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Todos los textos y etiquetas que aparecen en el PDF. Podés reemplazarlos por los tuyos o traducirlos.
              </p>
              {[
                ["passenger_label", "Etiqueta: Pasajero"],
                ["destination_label", "Etiqueta: Destino"],
                ["passengers_label", "Etiqueta: Cant. Pasajeros"],
                ["flights_overline", "Overline sección Vuelos"],
                ["flights_title", "Título sección Vuelos"],
                ["flight_price_pp_label", "Vuelo: precio por persona"],
                ["flight_price_total_label", "Vuelo: precio total"],
                ["hotels_overline", "Overline sección Hoteles"],
                ["hotels_title", "Título sección Hoteles"],
                ["hotel_price_pp_label", "Hotel: precio por persona"],
                ["hotel_price_total_label", "Hotel: precio total"],
                ["total_label", "Etiqueta Total Cotización"],
                ["notes_overline", "Overline sección Notas"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
                  <Input value={s[key] ?? ""} onChange={(e) => set({ [key]: e.target.value })} className="rounded-lg h-9 text-sm" data-testid={`label-${key}`} />
                </div>
              ))}
            </TabsContent>

            <TabsContent value="page" className="space-y-4 pt-4">
              <ColorField label="Color de fondo de página" value={s.page_bg_color} onChange={(v) => set({ page_bg_color: v })} testId="color-page-bg" />
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Se aplica sólo si no hay plantilla de fondo cargada.
              </p>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                <p className="text-xs font-semibold text-slate-700">Mostrar número de página</p>
                <Switch checked={!!s.show_page_number} onCheckedChange={(v) => set({ show_page_number: v })} data-testid="switch-show-page-num" />
              </div>
            </TabsContent>
          </Tabs>
        </aside>
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <p className="font-semibold text-slate-900">Vista previa del PDF</p>
              <div className="flex gap-2">
                {previewUrl && <a href={previewUrl} download="cotizacion_muestra.pdf">
                  <Button variant="outline" size="sm" className="rounded-lg"><Download size={14} className="mr-1" />Descargar</Button>
                </a>}
                <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center text-slate-500 gap-2">
                  <Loader2 className="animate-spin" size={18} /> Generando...
                </div>
              ) : previewUrl ? (
                <iframe title="preview" src={previewUrl} className="w-full h-full" />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
